// src/pages/coach/CoachDashboard.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import API from '../../api';
import { useCurrentUser } from '../../context/UserContext';
import { formatDate, formatTime } from '../../utils/date';
import { ListLoadingSpinner } from '../../components/layout/ListPageLayout';
import SessionHostAction from '../../components/classroom/SessionHostAction';
import {
    ChartPanel,
    CompareBar,
    CompositionDonut,
    ScoreGauge,
    CHART_COLORS,
} from '../../components/charts';
import { localizeHoursByWeekday } from '../../utils/chartSeries';
import { localizeBatchName } from '../../utils/displayNames';

const fmtPct = (v) => (v == null ? '-' : `${Number(v).toFixed(1)}%`);

// "Name #ID" helper; falls back gracefully if one side missing
const fmtNameId = (name, id) => {
    const idPart = id ? `(#${id})` : '';
    const nm = name ?? '';
    const text = `${nm} ${idPart}`.trim();
    return text || '-';
};

const CoachDashboard = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { user } = useCurrentUser();

    // KPIs
    const [kpiLoading, setKpiLoading] = useState(false);
    const [kpiError, setKpiError] = useState('');
    const [kpis, setKpis] = useState({
        sessionsToday: 0,
        studentsToday: 0,
        hoursThisWeek: 0,
        attendanceRateWTD: null,
        noShowsToday: 0,
        presentThisWeek: 0,
        absentThisWeek: 0,
        hoursByWeekday: [],
    });

    // Today's sessions
    const [todayLoading, setTodayLoading] = useState(false);
    const [todayErr, setTodayErr] = useState('');
    const [todaySessions, setTodaySessions] = useState([]);

    // Upcoming (next 7 days)
    const [upcomingLoading, setUpcomingLoading] = useState(false);
    const [upcomingErr, setUpcomingErr] = useState('');
    const [upcomingSessions, setUpcomingSessions] = useState([]);

    useEffect(() => {
        const ac = new AbortController();

        // Load KPIs
        const loadKpis = async () => {
            setKpiLoading(true);
            setKpiError('');
            try {
                const res = await API.get('/coach/metrics', { signal: ac.signal });
                const m = res?.data || {};
                setKpis({
                    sessionsToday: Number(m.sessionsToday ?? 0),
                    studentsToday: Number(m.studentsToday ?? 0),
                    hoursThisWeek: Number(m.hoursThisWeek ?? 0),
                    attendanceRateWTD: m.attendanceRateWTD ?? null,
                    noShowsToday: Number(m.noShowsToday ?? 0),
                    presentThisWeek: Number(m.presentThisWeek ?? 0),
                    absentThisWeek: Number(m.absentThisWeek ?? 0),
                    hoursByWeekday: localizeHoursByWeekday(
                        Array.isArray(m.hoursByWeekday) ? m.hoursByWeekday : [],
                        t,
                    ),
                });
            } catch (e) {
                setKpiError(t('pages.coachDashboard.metricsFailed'));
            } finally {
                setKpiLoading(false);
            }
        };

        // Load today's sessions
        const loadToday = async () => {
            setTodayLoading(true);
            setTodayErr('');
            try {
                const res = await API.get('/coach/sessions/today', { signal: ac.signal });
                setTodaySessions(Array.isArray(res?.data) ? res.data : []);
            } catch (e) {
                setTodayErr(t('pages.coachDashboard.todayFailed'));
            } finally {
                setTodayLoading(false);
            }
        };

        // Load upcoming
        const loadUpcoming = async () => {
            setUpcomingLoading(true);
            setUpcomingErr('');
            try {
                const res = await API.get('/coach/sessions/upcoming?days=7', { signal: ac.signal });
                setUpcomingSessions(Array.isArray(res?.data) ? res.data : []);
            } catch (e) {
                setUpcomingErr(t('pages.coachDashboard.upcomingFailed'));
            } finally {
                setUpcomingLoading(false);
            }
        };

        loadKpis();
        loadToday();
        loadUpcoming();

        const poll = window.setInterval(() => {
            API.get('/coach/sessions/today')
                .then((res) => setTodaySessions(Array.isArray(res?.data) ? res.data : []))
                .catch(() => {});
            API.get('/coach/sessions/upcoming?days=7')
                .then((res) => setUpcomingSessions(Array.isArray(res?.data) ? res.data : []))
                .catch(() => {});
        }, 20000);

        return () => {
            ac.abort();
            window.clearInterval(poll);
        };
    }, [t]);

    const handleMarkAttendance = (sessionId) => {
        openAttendance(sessionId, false);
    };

    const handleViewAttendance = (sessionId) => {
        openAttendance(sessionId, true);
    };

    const openAttendance = (sessionId, viewOnly) => {
        const session = [...todaySessions, ...upcomingSessions].find(s => s.sessionId === sessionId);
        const path = viewOnly
            ? `/coach/sessions/${sessionId}/attendance/view`
            : `/coach/sessions/${sessionId}/attendance`;
        if (session) {
            navigate(path, {
                state: {
                    sessionId: session.sessionId,
                    batchId: session.batchId,
                    levelName: session.levelName,
                    batchName: session.batchName,
                    branchName: session.branchName,
                    startTime: session.startTime,
                    endTime: session.endTime,
                }
            });
        } else {
            navigate(path);
        }
    };

    const handleViewCalendar = () => {
        navigate('/coach/sessions');
    };

    const upcomingGrouped = useMemo(() => {
        const map = new Map();
        for (const s of upcomingSessions) {
            const day = (s.startTime || '').slice(0, 10); // "YYYY-MM-DD"
            if (!map.has(day)) map.set(day, []);
            map.get(day).push(s);
        }
        return map;
    }, [upcomingSessions]);

    const renderDay = (dayISO) => {
        const text = formatDate(dayISO);
        return text ? text.replace(/\//g, '-') : dayISO;
    };

    const MetricCard = ({ title, value, icon, gradient }) => (
        <div className={`bg-gradient-to-br ${gradient} rounded-2xl shadow-lg p-6 text-white`}>
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-sm opacity-90 mb-1">{title}</div>
                    <div className="text-3xl font-bold">{kpiLoading ? '...' : value}</div>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    {icon}
                </div>
            </div>
        </div>
    );

    return (
        <div className="page-shell">

            {/* Welcome Header */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-yellow-600 bg-clip-text text-transparent">
                    {t('dashboard.welcome', { name: user?.firstName || t('dashboard.defaultCoach') })}
                </h1>
                <p className="text-gray-600 mt-2">
                    {t('pages.coachDashboard.subtitle')}
                </p>
            </div>

            {/* KPIs */}
            {kpiError ? (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-600">{kpiError}</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <MetricCard
                        title={t('pages.coachDashboard.sessionsToday')}
                        value={kpis.sessionsToday}
                        gradient="from-blue-500 to-blue-600"
                        icon={
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        }
                    />
                    <MetricCard
                        title={t('pages.coachDashboard.studentsToday')}
                        value={kpis.studentsToday}
                        gradient="from-green-500 to-emerald-600"
                        icon={
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        }
                    />
                    <MetricCard
                        title={t('pages.coachDashboard.hoursThisWeek')}
                        value={kpis.hoursThisWeek}
                        gradient="from-purple-500 to-purple-600"
                        icon={
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        }
                    />
                    <MetricCard
                        title={t('pages.coachDashboard.attendanceWtd')}
                        value={fmtPct(kpis.attendanceRateWTD)}
                        gradient="from-orange-500 to-orange-600"
                        icon={
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        }
                    />
                    <MetricCard
                        title={t('pages.coachDashboard.noShowsToday')}
                        value={kpis.noShowsToday}
                        gradient="from-red-500 to-red-600"
                        icon={
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        }
                    />
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
                <ChartPanel
                    title={t('charts.attendanceWtd', { defaultValue: 'Attendance this week' })}
                    loading={kpiLoading}
                    empty={!kpiLoading && kpis.attendanceRateWTD == null}
                    emptyMessage={t('charts.noAttendanceYet', { defaultValue: 'No completed sessions this week yet' })}
                    error={kpiError || null}
                    height={240}
                >
                    <ScoreGauge
                        value={kpis.attendanceRateWTD ?? 0}
                        label={t('pages.coachDashboard.attendanceWtd')}
                        sublabel={t('charts.weekToDate', { defaultValue: 'Week to date' })}
                        color={CHART_COLORS.success}
                    />
                </ChartPanel>
                <ChartPanel
                    title={t('charts.hoursByWeekday', { defaultValue: 'Hours by weekday' })}
                    loading={kpiLoading}
                    empty={!kpiLoading && !(kpis.hoursByWeekday || []).some((r) => Number(r.value) > 0)}
                    emptyMessage={t('charts.noHoursYet', { defaultValue: 'No teaching hours logged this week' })}
                    error={kpiError || null}
                    height={240}
                >
                    <CompareBar data={kpis.hoursByWeekday || []} height={180} colorByIndex={false} />
                </ChartPanel>
                <ChartPanel
                    title={t('charts.presentVsAbsent', { defaultValue: 'Present vs absent' })}
                    subtitle={t('charts.completedSessionsWeek', { defaultValue: 'From completed sessions this week' })}
                    loading={kpiLoading}
                    empty={!kpiLoading && !kpis.presentThisWeek && !kpis.absentThisWeek}
                    emptyMessage={t('charts.noAttendanceMarks', { defaultValue: 'No attendance marks yet this week' })}
                    error={kpiError || null}
                    height={240}
                >
                    <CompositionDonut
                        data={[
                            { name: t('reports.common.present', { defaultValue: 'Present' }), value: kpis.presentThisWeek, color: CHART_COLORS.success },
                            { name: t('reports.common.absent', { defaultValue: 'Absent' }), value: kpis.absentThisWeek, color: CHART_COLORS.danger },
                        ]}
                        chartSize={180}
                        centerValue={fmtPct(kpis.attendanceRateWTD)}
                        centerLabel={t('charts.rate', { defaultValue: 'Rate' })}
                    />
                </ChartPanel>
            </div>

            {/* Today's sessions */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-900">{t('pages.coachDashboard.todaysSchedule')}</h2>
                    <button
                        type="button"
                        onClick={handleViewCalendar}
                        className="btn-page-action-info"
                    >
                        {t('pages.coachDashboard.viewAllSessions')}
                    </button>
                </div>
                <div className="p-6">
                    {todayLoading ? (
                        <ListLoadingSpinner />
                    ) : todayErr ? (
                        <div className="text-center py-8 text-red-600">{todayErr}</div>
                    ) : todaySessions.length === 0 ? (
                        <div className="text-center py-12">
                            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <p className="text-gray-500">{t('pages.coachDashboard.noSessionsToday')}</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full">
                                <thead className="table-header">
                                    <tr>
                                        <th className="table-header-cell">{t('table.time')}</th>
                                        <th className="table-header-cell">{t('table.branch')}</th>
                                        <th className="table-header-cell">{t('table.level')}</th>
                                        <th className="table-header-cell">{t('table.batch')}</th>
                                        <th className="table-header-cell">{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {todaySessions.map((s) => (
                                        <tr key={s.sessionId} className="table-row">
                                            <td className="table-cell font-medium text-gray-900">
                                                {formatTime(s.startTime)} - {formatTime(s.endTime)}
                                            </td>
                                            <td className="table-cell">{fmtNameId(s.branchName, s.branchId)}</td>
                                            <td className="table-cell">{fmtNameId(s.levelName, s.levelId)}</td>
                                            <td className="table-cell">{fmtNameId(localizeBatchName(s.batchName, t), s.batchId)}</td>
                                            <td className="table-cell whitespace-nowrap">
                                                <div className="inline-flex items-center justify-start gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => (s.attendanceMarked
                                                            ? handleViewAttendance(s.sessionId)
                                                            : handleMarkAttendance(s.sessionId))}
                                                        className={`${s.attendanceMarked ? 'btn-action-view' : 'btn-action-activate'} inline-flex items-center justify-center min-w-[8.75rem] h-8`}
                                                    >
                                                        {s.attendanceMarked ? t('pages.coachDashboard.viewAttendance') : t('pages.coachDashboard.markAttendance')}
                                                    </button>
                                                    <SessionHostAction
                                                        sessionId={s.sessionId}
                                                        role="COACH"
                                                        classroomEligible={s.classroomEligible}
                                                        hasMeeting={s.hasMeeting}
                                                        meetingStatus={s.meetingStatus}
                                                        status={s.status}
                                                        endTime={s.endTime}
                                                        compact
                                                        aligned
                                                        onStarted={() => {
                                                            const patch = (r) => (
                                                                r.sessionId === s.sessionId
                                                                    ? { ...r, meetingStatus: 'STARTED', hasMeeting: true }
                                                                    : r
                                                            );
                                                            setTodaySessions((rows) => rows.map(patch));
                                                            setUpcomingSessions((rows) => rows.map(patch));
                                                        }}
                                                        onEnded={() => {
                                                            const patch = (r) => (
                                                                r.sessionId === s.sessionId
                                                                    ? { ...r, meetingStatus: 'ENDED', hasMeeting: true, canJoin: false }
                                                                    : r
                                                            );
                                                            setTodaySessions((rows) => rows.map(patch));
                                                            setUpcomingSessions((rows) => rows.map(patch));
                                                        }}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Upcoming */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-900">{t('pages.coachDashboard.upcoming7Days')}</h2>
                </div>
                <div className="p-6">
                    {upcomingLoading ? (
                        <ListLoadingSpinner />
                    ) : upcomingErr ? (
                        <div className="text-center py-8 text-red-600">{upcomingErr}</div>
                    ) : upcomingGrouped.size === 0 ? (
                        <div className="text-center py-12">
                            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <p className="text-gray-500">{t('pages.coachDashboard.noUpcomingSessions')}</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {[...upcomingGrouped.entries()].map(([day, rows]) => (
                                <div key={day} className="border border-gray-200 rounded-xl overflow-hidden">
                                    <div className="table-header px-4 py-3">
                                        <span className="text-sm font-semibold text-purple-900">{renderDay(day)}</span>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full">
                                            <thead className="table-header">
                                                <tr>
                                                    <th className="table-header-cell">{t('table.time')}</th>
                                                    <th className="table-header-cell">{t('table.branch')}</th>
                                                    <th className="table-header-cell">{t('table.level')}</th>
                                                    <th className="table-header-cell">{t('table.batch')}</th>
                                                    <th className="table-header-cell">{t('common.actions')}</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {rows.map((s) => (
                                                    <tr key={s.sessionId} className="table-row">
                                                        <td className="table-cell font-medium text-gray-900">
                                                            {formatTime(s.startTime)} - {formatTime(s.endTime)}
                                                        </td>
                                                        <td className="table-cell">{fmtNameId(s.branchName, s.branchId)}</td>
                                                        <td className="table-cell">{fmtNameId(s.levelName, s.levelId)}</td>
                                                        <td className="table-cell">{fmtNameId(localizeBatchName(s.batchName, t), s.batchId)}</td>
                                                        <td className="table-cell whitespace-nowrap">
                                                            <div className="inline-flex items-center justify-start gap-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => (s.attendanceMarked
                                                                        ? handleViewAttendance(s.sessionId)
                                                                        : handleMarkAttendance(s.sessionId))}
                                                                    className={`${s.attendanceMarked ? 'btn-action-view' : 'btn-action-activate'} inline-flex items-center justify-center min-w-[8.75rem]`}
                                                                >
                                                                    {s.attendanceMarked ? t('pages.coachDashboard.viewAttendance') : t('pages.coachDashboard.markAttendance')}
                                                                </button>
                                                                <SessionHostAction
                                                                    sessionId={s.sessionId}
                                                                    role="COACH"
                                                                    classroomEligible={s.classroomEligible}
                                                                    hasMeeting={s.hasMeeting}
                                                                    meetingStatus={s.meetingStatus}
                                                                    status={s.status}
                                                                    endTime={s.endTime}
                                                                    compact
                                                                    aligned
                                                                    onStarted={() => {
                                                                        const patch = (r) => (
                                                                            r.sessionId === s.sessionId
                                                                                ? { ...r, meetingStatus: 'STARTED', hasMeeting: true }
                                                                                : r
                                                                        );
                                                                        setTodaySessions((rows) => rows.map(patch));
                                                                        setUpcomingSessions((rows) => rows.map(patch));
                                                                    }}
                                                                    onEnded={() => {
                                                                        const patch = (r) => (
                                                                            r.sessionId === s.sessionId
                                                                                ? { ...r, meetingStatus: 'ENDED', hasMeeting: true, canJoin: false }
                                                                                : r
                                                                        );
                                                                        setTodaySessions((rows) => rows.map(patch));
                                                                        setUpcomingSessions((rows) => rows.map(patch));
                                                                    }}
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CoachDashboard;
